/**
 * iCal Calendar Generator
 * Generates .ics files for case events to import into calendar applications
 */

import ical from "ical-generator";

export interface CalendarEvent {
  id: string;
  case_id: string;
  event_type: string;
  event_title: string;
  event_description?: string | null;
  event_date: string;
  triggered_by: string;
  event_data?: Record<string, unknown>;
}

export interface GenerateCalendarOptions {
  caseId: string;
  caseTitle: string;
  events: CalendarEvent[];
  country?: string;
}

/**
 * Get timezone for country
 */
function getCountryTimezone(country?: string): string {
  const timezones: Record<string, string> = {
    MX: "America/Mexico_City",
    RD: "America/Santo_Domingo",
    CO: "America/Bogota",
    CL: "America/Santiago",
    PE: "America/Lima",
  };

  return timezones[country || "MX"] || "America/Mexico_City";
}

/**
 * Generate iCal calendar from case events
 */
export function generateCaseCalendar(
  options: GenerateCalendarOptions
): string {
  const { caseId, caseTitle, events, country } = options;

  const timezone = getCountryTimezone(country);

  const calendar = ical({
    name: "Lawgentia - Casos Legales",
    description: `Eventos del caso: ${caseTitle}`,
    timezone: timezone,
    prodId: {
      company: "Nolim Studios",
      product: "Lawgentia",
      language: "ES",
    },
    url: typeof window !== "undefined" 
      ? `${window.location.origin}/dashboard/cases/${caseId}`
      : undefined,
  });

  events.forEach((event) => {
    try {
      const eventDate = new Date(event.event_date);

      calendar.createEvent({
        start: eventDate,
        end: new Date(eventDate.getTime() + 60 * 60 * 1000), // 1 hour duration
        summary: event.event_title,
        description: event.event_description || "",
        url:
          typeof window !== "undefined"
            ? `${window.location.origin}/dashboard/cases/${caseId}`
            : undefined,
        location: undefined,
      });
    } catch (error) {
      console.error(`Error creating calendar event for ${event.id}:`, error);
    }
  });

  return calendar.toString();
}

/**
 * Download calendar file
 */
export function downloadCalendarFile(
  calendarContent: string,
  filename: string
): void {
  if (typeof window === "undefined") {
    console.error("downloadCalendarFile can only be called in browser");
    return;
  }

  const blob = new Blob([calendarContent], {
    type: "text/calendar;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

